/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { useConversationStream } from './use-conversation-stream';
import ConversationMessage from './conversation-message';
import MessageComposer from './message-composer';

/** Per-co-pilot chat presentation. Defaults make it generic; Phase B fills it from
 *  the agent so the same shell reskins for any co-pilot (e.g. child-protection). */
export interface ChatUiConfig {
  title?: string;
  icon?: string;
  accentColor?: string;
  welcomeTitle?: string;
  welcomeText?: string;
  starters?: string[];
  showSources?: boolean;
  showFeedback?: boolean;
}

interface AgentLike {
  name?: string | null;
  agentCode?: string;
  description?: string | null;
  conversation?: {
    ui?: {
      title?: string | null;
      icon?: string | null;
      accentColor?: string | null;
      welcomeTitle?: string | null;
      welcomeText?: string | null;
      starters?: string[] | null;
      showSources?: boolean;
      showFeedback?: boolean;
    } | null;
  } | null;
}

/** Build the chat UI config from an agent's conversation.ui, with sensible fallbacks.
 *  Shared by the single conversation view and the chat workspace so both reskin alike. */
export function buildChatUi(agent?: AgentLike | null): ChatUiConfig {
  const cfg = agent?.conversation?.ui || {};
  const title = cfg.title || agent?.name || agent?.agentCode || 'Co-pilot';
  return {
    title,
    icon: cfg.icon || 'pi pi-android',
    accentColor: cfg.accentColor || undefined,
    welcomeTitle: cfg.welcomeTitle || (agent ? `Chat with ${title}` : 'Start the conversation'),
    welcomeText: cfg.welcomeText || agent?.description
      || 'Ask a question to begin. Answers are grounded in this co-pilot’s knowledge base.',
    starters: cfg.starters || [],
    showSources: cfg.showSources !== false,
    showFeedback: cfg.showFeedback !== false,
  };
}

interface Props {
  conversationId: string;
  agentCode: string;
  canSend: boolean;
  ui?: ChatUiConfig;
  /** Called after the first turn completes, so a host can refresh the title/list. */
  onTurnDone?: () => void;
}

/** The core chat surface — thread + composer + streaming — with no page chrome.
 *  Reused by the single conversation view and the full chat workspace. */
const ConversationThread = ({ conversationId, agentCode, canSend, ui, onTurnDone }: Props) => {
  const toast = useRef<Toast>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { messages, loading, streaming, error, send } = useConversationStream(conversationId);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (error) toast.current?.show({ severity: 'warn', summary: 'Turn failed', detail: error, life: 8000 });
  }, [error]);

  // Notify the host when a turn finishes (title may now be set from the first message).
  const wasStreaming = useRef(false);
  useEffect(() => {
    if (wasStreaming.current && !streaming) onTurnDone?.();
    wasStreaming.current = streaming;
  }, [streaming, onTurnDone]);

  const starters = (ui?.starters || []).filter(Boolean);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Toast ref={toast} position="top-right" />

      <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-5">
        {loading && !messages.length ? (
          <p className="text-sm text-ink-muted">Loading messages…</p>
        ) : messages.length === 0 ? (
          <div className="mx-auto flex max-w-xl flex-col items-center gap-3 pt-10 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <i className="pi pi-comments text-xl" aria-hidden />
            </div>
            <h3 className="m-0 text-base font-semibold text-gray-900 dark:text-gray-100">
              {ui?.welcomeTitle || 'Start the conversation'}
            </h3>
            <p className="m-0 text-sm text-ink-muted">
              {ui?.welcomeText || 'Ask a question to begin. Answers are grounded in this agent’s knowledge base.'}
            </p>
            {starters.length > 0 && canSend && (
              <div className="mt-2 flex flex-wrap justify-center gap-2">
                {starters.map((s) => (
                  <button key={s} type="button" onClick={() => send(s)}
                    className="ui-press rounded-full border border-line px-3 py-1.5 text-xs text-gray-700 hover:border-primary hover:text-primary dark:text-gray-300">
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          messages.map((m) => (
            <ConversationMessage key={m._id} message={m} conversationId={conversationId} agentCode={agentCode}
              showSources={ui?.showSources !== false} showFeedback={ui?.showFeedback !== false}
              assistantIcon={ui?.icon} accentColor={ui?.accentColor} />
          ))
        )}
      </div>

      {canSend ? (
        <MessageComposer onSend={send} disabled={streaming} />
      ) : (
        <p className="border-t border-gray-200 p-3 text-center text-sm text-ink-muted dark:border-gray-700">
          You don&rsquo;t have permission to send messages here.
        </p>
      )}
    </div>
  );
};

export default ConversationThread;
